import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Copy, CheckCircle, Link, Key, RefreshCw, Search } from 'lucide-react'
import { apiClientsApi, ApiClient, ApiClientCreate, ApiClientUpdate, FactusolFamily } from '@/lib/api'
import { cn } from '@/lib/utils'

interface ApiClientModalProps {
  client: ApiClient | null
  onClose: () => void
}

export default function ApiClientModal({ client, onClose }: ApiClientModalProps) {
  const queryClient = useQueryClient()
  const isEdit = !!client

  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    activo: true,
    fecha_expiracion: '',
    limite_descargas_dia: 0,
    ips_permitidas: '',
    tarifa: 1,
    incluir_iva: true,
    familias: [] as string[]
  })

  const [copiedToken, setCopiedToken] = useState(false)
  const [copiedUrl, setCopiedUrl] = useState(false)
  const [familySearch, setFamilySearch] = useState('')

  // Load Factusol families
  const { data: familiesData, isLoading: familiesLoading } = useQuery({
    queryKey: ['factusol-families'],
    queryFn: () => apiClientsApi.getFamilies(),
    staleTime: 5 * 60 * 1000,
  })

  const families: FactusolFamily[] = familiesData?.families || []

  useEffect(() => {
    if (client) {
      setFormData({
        nombre: client.nombre,
        email: client.email || '',
        activo: client.activo,
        fecha_expiracion: client.fecha_expiracion ? client.fecha_expiracion.split('T')[0] : '',
        limite_descargas_dia: client.limite_descargas_dia,
        ips_permitidas: client.ips_permitidas?.join(', ') || '',
        tarifa: client.tarifa || 1,
        incluir_iva: client.incluir_iva !== false,
        familias: client.familias || []
      })
    }
  }, [client])

  const createMutation = useMutation({
    mutationFn: (data: ApiClientCreate) => apiClientsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-clients'] })
      queryClient.invalidateQueries({ queryKey: ['api-clients-stats'] })
      onClose()
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ApiClientUpdate }) =>
      apiClientsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-clients'] })
      queryClient.invalidateQueries({ queryKey: ['api-clients-stats'] })
      onClose()
    }
  })

  const regenerateTokenMutation = useMutation({
    mutationFn: apiClientsApi.regenerateToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-clients'] })
    }
  })

  const isPending = createMutation.isPending || updateMutation.isPending

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const ipsArray = formData.ips_permitidas
      .split(',')
      .map(ip => ip.trim())
      .filter(ip => ip.length > 0)

    const payload = {
      nombre: formData.nombre,
      email: formData.email || null,
      activo: formData.activo,
      fecha_expiracion: formData.fecha_expiracion || null,
      limite_descargas_dia: formData.limite_descargas_dia,
      ips_permitidas: ipsArray.length > 0 ? ipsArray : null,
      tarifa: formData.tarifa,
      incluir_iva: formData.incluir_iva,
      familias: formData.familias.length > 0 ? formData.familias : null
    }

    if (isEdit && client) {
      updateMutation.mutate({ id: client.id, data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const copyToClipboard = async (text: string, type: 'token' | 'url') => {
    await navigator.clipboard.writeText(text)
    if (type === 'token') {
      setCopiedToken(true)
      setTimeout(() => setCopiedToken(false), 2000)
    } else {
      setCopiedUrl(true)
      setTimeout(() => setCopiedUrl(false), 2000)
    }
  }

  const toggleFamily = (code: string) => {
    setFormData(prev => ({
      ...prev,
      familias: prev.familias.includes(code)
        ? prev.familias.filter(f => f !== code)
        : [...prev.familias, code]
    }))
  }

  const filteredFamilies = families.filter(f => {
    if (!familySearch) return true
    const q = familySearch.toLowerCase()
    return f.code.toLowerCase().includes(q) || f.name.toLowerCase().includes(q)
  })

  const exportUrl = client ? apiClientsApi.getExportUrl(client.token) : ''

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Editar Cliente API' : 'Nuevo Cliente API'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-500 hover:text-gray-700 hover:bg-slate-100 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Nombre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre del cliente *
            </label>
            <input
              type="text"
              value={formData.nombre}
              onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-400"
              placeholder="Ej: Bot IA Externo"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email de contacto
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-400"
              placeholder="contacto@empresa.com"
            />
          </div>

          {/* Activo */}
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.activo}
                onChange={(e) => setFormData(prev => ({ ...prev, activo: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
            <span className="text-sm text-gray-700">Cliente activo</span>
          </div>

          {/* Fecha expiracion */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha de expiracion
            </label>
            <input
              type="date"
              value={formData.fecha_expiracion}
              onChange={(e) => setFormData(prev => ({ ...prev, fecha_expiracion: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
            />
            <p className="text-xs text-slate-500 mt-1">Vacio = sin expiracion</p>
          </div>

          {/* Limite descargas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Limite de descargas por dia
            </label>
            <input
              type="number"
              min="0"
              value={formData.limite_descargas_dia}
              onChange={(e) => setFormData(prev => ({ ...prev, limite_descargas_dia: parseInt(e.target.value) || 0 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
            />
            <p className="text-xs text-slate-500 mt-1">0 = ilimitado</p>
          </div>

          {/* IPs permitidas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              IPs permitidas
            </label>
            <textarea
              value={formData.ips_permitidas}
              onChange={(e) => setFormData(prev => ({ ...prev, ips_permitidas: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 font-mono text-sm placeholder-gray-400"
              placeholder="192.168.1.1, 10.0.0.1"
            />
            <p className="text-xs text-slate-500 mt-1">Separadas por coma. Vacio = cualquier IP</p>
          </div>

          {/* Tarifa */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tarifa de precios
            </label>
            <select
              value={formData.tarifa}
              onChange={(e) => setFormData(prev => ({ ...prev, tarifa: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
            >
              <option value={1}>PVP (Tarifa Principal)</option>
              <option value={2}>PVD (Distribuidores)</option>
              <option value={3}>VIP</option>
            </select>
            <p className="text-xs text-slate-500 mt-1">Tarifa que se usara para los precios del CSV</p>
          </div>

          {/* Incluir IVA */}
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.incluir_iva}
                onChange={(e) => setFormData(prev => ({ ...prev, incluir_iva: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
            <div>
              <span className="text-sm text-gray-700">Incluir IVA en precios</span>
              <p className="text-xs text-slate-500">La cabecera del CSV indicara si incluye IVA o no</p>
            </div>
          </div>

          {/* Familias */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Familias de productos
            </label>
            {formData.familias.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {formData.familias.map(code => {
                  const fam = families.find(f => f.code === code)
                  return (
                    <span
                      key={code}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 rounded-md text-xs font-medium border border-primary-200"
                    >
                      {code} - {fam?.name || '...'}
                      <button
                        type="button"
                        onClick={() => toggleFamily(code)}
                        className="text-primary-400 hover:text-primary-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )
                })}
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, familias: [] }))}
                  className="text-xs text-slate-500 hover:text-red-600 px-1"
                >
                  Quitar todas
                </button>
              </div>
            )}
            <div className="relative mb-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={familySearch}
                onChange={(e) => setFamilySearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-400 text-sm"
                placeholder="Buscar familia por codigo o nombre..."
              />
            </div>
            <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
              {familiesLoading ? (
                <div className="p-3 text-sm text-slate-500 text-center">Cargando familias...</div>
              ) : filteredFamilies.length === 0 ? (
                <div className="p-3 text-sm text-slate-500 text-center">Sin resultados</div>
              ) : (
                filteredFamilies.map(f => (
                  <label
                    key={f.code}
                    className={cn(
                      "flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50 text-sm",
                      formData.familias.includes(f.code) && "bg-primary-50"
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={formData.familias.includes(f.code)}
                      onChange={() => toggleFamily(f.code)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="font-mono text-xs text-slate-500 w-8">{f.code}</span>
                    <span className="text-gray-700 truncate">{f.name}</span>
                  </label>
                ))
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {formData.familias.length === 0
                ? 'Sin seleccion = todas las familias'
                : `${formData.familias.length} familia${formData.familias.length !== 1 ? 's' : ''} seleccionada${formData.familias.length !== 1 ? 's' : ''}`
              }
            </p>
          </div>

          {/* Token info - only for edit */}
          {isEdit && client && (
            <div className="pt-4 border-t space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Key className="h-4 w-4 inline mr-1" />
                  Token de acceso
                </label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-gray-800 rounded-lg font-mono text-sm break-all text-green-400 border border-gray-600">
                    {client.token}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(client.token, 'token')}
                    className="p-2 text-slate-500 hover:text-gray-700 hover:bg-slate-100 rounded-lg flex-shrink-0"
                    title="Copiar token"
                  >
                    {copiedToken ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <Copy className="h-5 w-5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm('Regenerar token? El token actual dejara de funcionar.')) {
                        regenerateTokenMutation.mutate(client.id)
                      }
                    }}
                    className="p-2 text-orange-500 hover:text-orange-700 hover:bg-orange-50 rounded-lg flex-shrink-0"
                    title="Regenerar token"
                    disabled={regenerateTokenMutation.isPending}
                  >
                    <RefreshCw className={cn("h-5 w-5", regenerateTokenMutation.isPending && "animate-spin")} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Link className="h-4 w-4 inline mr-1" />
                  URL de acceso al catalogo
                </label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-gray-800 rounded-lg font-mono text-xs break-all text-cyan-400 border border-gray-600">
                    {exportUrl}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(exportUrl, 'url')}
                    className="p-2 text-slate-500 hover:text-gray-700 hover:bg-slate-100 rounded-lg flex-shrink-0"
                    title="Copiar URL"
                  >
                    {copiedUrl ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <Copy className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Error messages */}
          {(createMutation.error || updateMutation.error) && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              Error al guardar: {(createMutation.error || updateMutation.error)?.message}
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isPending || !formData.nombre}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
